/*
 * @Description: 省市区数据管理 Vuex Module
 * @Author: yuanmc
 * @Date: 2025-10-29 14:30:00
 * @LastEditTime: 2025-10-29 14:30:00
 * @LastEditors: yuanmc
 */

import { getCityData } from '@/api/waybillAI';

/**
 * 转换城市数据为级联选择器支持的格式
 * 从 {code, name, level, districts} 转换为 {label, value, children}
 */
function transformCityData(data) {
  if (!data || !Array.isArray(data)) {
    return [];
  }

  return data.map(item => {
    const transformed = {
      label: item.name,
      value: item.name, // 使用 name 作为 value
      // 保留原始数据，方便后续使用
      code: item.code,
      level: item.level,
    };

    // 递归处理子级数据（districts 是嵌套的对象数组）
    if (item.districts && Array.isArray(item.districts) && item.districts.length > 0) {
      transformed.children = transformCityData(item.districts);
    }

    return transformed;
  });
}

const state = {
  // 省市区数据
  cityData: [],
  // 是否正在加载
  loading: false,
  // 是否已加载
  loaded: false,
  // 加载错误信息
  error: null,
};

const mutations = {
  SET_CITY_DATA(state, data) {
    state.cityData = data;
    state.loaded = true;
    state.error = null;
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_ERROR(state, error) {
    state.error = error;
    state.loading = false;
  },
  CLEAR_CITY_DATA(state) {
    state.cityData = [];
    state.loaded = false;
    state.error = null;
  },
};

const actions = {
  /**
   * 初始化省市区数据
   * 根据是否是页面刷新来决定是否重新加载
   */
  async initCityData({ commit, state, dispatch }) {
    // 检查是否是页面刷新
    const wasLoaded = sessionStorage.getItem('cityDataLoaded');

    if (wasLoaded === 'true') {
      // 是刷新操作，重新加载数据
      console.log('检测到页面刷新，重新加载省市区数据');
      return dispatch('fetchCityData', true);
    } else if (state.loaded && state.cityData.length > 0) {
      // 数据已加载且不是刷新，直接返回
      console.log('省市区数据已存在，无需重新加载');
      return state.cityData;
    } else {
      // 首次加载
      console.log('首次加载省市区数据');
      return dispatch('fetchCityData');
    }
  },

  /**
   * 获取省市区数据
   * @param {Boolean} force - 是否强制刷新
   */
  async fetchCityData({ commit, state }, force = false) {
    // 如果已经在加载中，直接返回
    if (state.loading) {
      console.log('省市区数据正在加载中...');
      return state.cityData;
    }

    // 如果数据已存在且不是强制刷新，直接返回
    if (state.loaded && state.cityData.length > 0 && !force) {
      return state.cityData;
    }

    commit('SET_LOADING', true);

    try {
      // 新接口不需要 businessId 参数
      const { code, data } = await getCityData();

      if ((code === '00000' || code === 200) && data) {
        // 转换数据格式为级联选择器支持的格式
        const transformedData = transformCityData(data);
        commit('SET_CITY_DATA', transformedData);
        // 标记数据已加载
        sessionStorage.setItem('cityDataLoaded', 'true');
        return transformedData;
      } else {
        throw new Error('获取省市区数据失败');
      }
    } catch (error) {
      console.error('获取省市区数据失败:', error);
      commit('SET_ERROR', error.message || '获取省市区数据失败');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * 标记为正常导航（在路由离开时调用）
   */
  markAsNavigated() {
    if (sessionStorage.getItem('cityDataLoaded') === 'true') {
      sessionStorage.setItem('cityDataLoaded', 'navigated');
    }
  },

  /**
   * 清除省市区数据
   */
  clearCityData({ commit }) {
    commit('CLEAR_CITY_DATA');
    sessionStorage.removeItem('cityDataLoaded');
  },
};

const getters = {
  cityData: state => state.cityData,
  cityLoading: state => state.loading,
  cityLoaded: state => state.loaded,
  cityError: state => state.error,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
